import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import { mountTaskModalShell, unmountTaskModalShell } from "../modules/task_modal_shell"

export default class extends Controller {
  static targets = [
    "schedulesContainer",
    "duedateContainer",
    "statusSelect",
    "addScheduleButton",
    "scheduleContainer",
    "destroyScheduleButton",
    "editableField",
    "trixContainer",
    "duedateInput",
    "scheduleTemplate",
    "duedateChangeModal",
    "duedateChangeCommentInput",
    "duedateChangeCommentHidden",
    "duedateChangeCommentError"
  ]
  static values = {
    scheduledLabel: String,
    scheduledValue: { type: String, default: "scheduled" },
    originalDuedate: String,
    commentBlank: String
  }

  connect() {
    this.skipDuedateCommentCheck = false
    this.formElement = this.element.querySelector("form")
    this.boundInterceptSubmit = this.interceptSubmit.bind(this)
    this.formElement?.addEventListener("submit", this.boundInterceptSubmit)
    if (this.element.classList.contains('task-form-modal')) {
      this._modalShell = mountTaskModalShell({ extraClass: 'task-modal--task-form' })
    }
    this.initExistingSchedules()
    this.checkTaskStatus()
    this.observeScheduleChanges()
  }

  disconnect() {
    unmountTaskModalShell(this._modalShell)
    this._modalShell = null
    this.formElement?.removeEventListener("submit", this.boundInterceptSubmit)
  }

  interceptSubmit(event) {
    if (this.skipDuedateCommentCheck) return
    if (!this.requiresDuedateChangeComment()) return

    if (!this.hasDuedateChangeModalTarget) return

    event.preventDefault()
    this.openDuedateChangeModal()
  }

  requiresDuedateChangeComment() {
    if (!this.hasOriginalDuedateValue || !this.originalDuedateValue) return false

    return this.currentDuedateValue() !== this.originalDuedateValue
  }

  currentDuedateValue() {
    if (this.hasDuedateInputTarget) {
      return this.duedateInputTarget.value || ""
    }
    return ""
  }

  openDuedateChangeModal() {
    if (!this.hasDuedateChangeModalTarget) return

    if (this.hasDuedateChangeCommentInputTarget) {
      this.clearDuedateChangeCommentInput()
      this.duedateChangeCommentInputTarget.classList.remove("is-invalid")
    }
    this.clearDuedateChangeCommentError()

    const modal = Modal.getOrCreateInstance(this.duedateChangeModalTarget)
    modal.show()
    this.focusDuedateChangeCommentInput()
  }

  confirmDuedateChange(event) {
    event.preventDefault()

    const plain = this.duedateChangeCommentPlainText()
    if (!plain) {
      if (this.hasDuedateChangeCommentInputTarget) {
        this.duedateChangeCommentInputTarget.classList.add("is-invalid")
      }
      if (this.hasDuedateChangeCommentErrorTarget) {
        this.duedateChangeCommentErrorTarget.textContent = this.commentBlankValue
        this.duedateChangeCommentErrorTarget.hidden = false
      }
      return
    }

    if (this.hasDuedateChangeCommentHiddenTarget) {
      this.duedateChangeCommentHiddenTarget.value = this.duedateChangeCommentHtml()
    }

    this.skipDuedateCommentCheck = true

    const modal = Modal.getInstance(this.duedateChangeModalTarget)
    modal?.hide()

    this.formElement?.requestSubmit()
  }

  clearDuedateChangeCommentError() {
    if (this.hasDuedateChangeCommentErrorTarget) {
      this.duedateChangeCommentErrorTarget.textContent = ""
      this.duedateChangeCommentErrorTarget.hidden = true
    }
    if (this.hasDuedateChangeCommentInputTarget) {
      this.duedateChangeCommentInputTarget.classList.remove("is-invalid")
    }
  }

  duedateChangeCommentPlainText() {
    if (!this.hasDuedateChangeCommentInputTarget) return ""

    const input = this.duedateChangeCommentInputTarget
    if (input.tagName === "TRIX-EDITOR" && input.editor) {
      return input.editor.getDocument().toString().trim()
    }

    return (input.value || "").trim()
  }

  duedateChangeCommentHtml() {
    if (!this.hasDuedateChangeCommentInputTarget) return ""

    const input = this.duedateChangeCommentInputTarget
    if (input.tagName === "TRIX-EDITOR") {
      const linkedId = input.getAttribute("input")
      const linked = linkedId ? document.getElementById(linkedId) : null
      return linked?.value || ""
    }

    return (input.value || "").trim()
  }

  clearDuedateChangeCommentInput() {
    if (!this.hasDuedateChangeCommentInputTarget) return

    const input = this.duedateChangeCommentInputTarget
    if (input.tagName === "TRIX-EDITOR" && input.editor) {
      input.editor.loadHTML("")
      return
    }

    input.value = ""
  }

  focusDuedateChangeCommentInput() {
    if (!this.hasDuedateChangeCommentInputTarget) return

    const input = this.duedateChangeCommentInputTarget
    if (input.tagName === "TRIX-EDITOR") {
      input.focus()
      return
    }

    input.focus()
  }
  
  // Check task status and update duedate/status field visibility
  checkTaskStatus() {
    const hasSchedules = this.visibleScheduleCount > 0
    
    // Hide duedate field when task has schedules or is scheduled (recurring tasks get duedate from schedule)
    if ((this.hasStatusSelectTarget && this.statusSelectTarget.value === 'scheduled') || hasSchedules) {
      this.hideDuedateField()
    }
    
    // Disable status field when task has scheduling - status must remain "scheduled"
    if (hasSchedules) {
      this.disableStatusField()
    } else {
      this.enableStatusField()
    }
    
    // Add status change listener
    if (this.hasStatusSelectTarget) {
      this.statusSelectTarget.addEventListener('change', (e) => {
        const hasSchedulesNow = this.visibleScheduleCount > 0
        if (e.target.value === 'scheduled' || hasSchedulesNow) {
          this.hideDuedateField()
        } else {
          this.showDuedateField()
        }
      })
    }
  }
  
  get visibleScheduleCount() {
    return this.scheduleContainerTargets.filter(el => !el.classList.contains('d-none')).length
  }
  
  // Disable task form fields
  disableMainFormFields() {
    if (this.hasEditableFieldTarget) {
      this.editableFieldTargets.forEach(field => field.setAttribute('disabled', 'disabled'))
    }
    const trixEditor = this.hasTrixContainerTarget ? this.trixContainerTarget.querySelector('trix-editor') : null
    if (trixEditor) trixEditor.setAttribute('disabled', 'disabled')
  }
  
  // Enable task form fields
  enableMainFormFields() {
    if (this.hasEditableFieldTarget) {
      this.editableFieldTargets.forEach(field => field.removeAttribute('disabled'))
    }
    const trixEditor = this.hasTrixContainerTarget ? this.trixContainerTarget.querySelector('trix-editor') : null
    if (trixEditor) trixEditor.removeAttribute('disabled')
  }
  
  // Hide due date field and set value to null (not required when schedules drive due date)
  hideDuedateField() {
    if (this.hasDuedateContainerTarget) {
      this.duedateContainerTarget.style.display = 'none'
      if (this.hasDuedateInputTarget) {
        this.duedateInputTarget.value = ''
        this.duedateInputTarget.required = false
      }
      const star = document.getElementById("task-duedate-required-star")
      if (star) star.style.display = "none"
    }
  }
  
  // Show due date field — required when no schedules / not scheduled status
  showDuedateField() {
    if (this.hasDuedateContainerTarget) {
      this.duedateContainerTarget.style.display = 'block'
      if (this.hasDuedateInputTarget) {
        this.duedateInputTarget.required = true
      }
      const star = document.getElementById("task-duedate-required-star")
      if (star) star.style.display = ""
    }
  }
  
  // Disable status field when task has scheduling - show only "scheduled", set value, disable
  disableStatusField() {
    if (!this.hasStatusSelectTarget) return
    const statusSelect = this.statusSelectTarget
    const scheduledValue = this.scheduledValueValue
    const scheduledLabel = this.scheduledLabelValue
    // Add scheduled option if not present, set as selected
    const hasScheduledOption = Array.from(statusSelect.options).some(opt => opt.value === scheduledValue)
    if (!hasScheduledOption && scheduledLabel) {
      const option = document.createElement('option')
      option.value = scheduledValue
      option.textContent = scheduledLabel
      statusSelect.appendChild(option)
    }
    statusSelect.value = scheduledValue
    statusSelect.setAttribute('disabled', 'disabled')
  }
  
  // Enable status field when task has no scheduling - remove "scheduled" option
  enableStatusField() {
    if (!this.hasStatusSelectTarget) return
    const statusSelect = this.statusSelectTarget
    const scheduledValue = this.scheduledValueValue
    // Remove scheduled option
    Array.from(statusSelect.options).filter(opt => opt.value === scheduledValue).forEach(opt => opt.remove())
    // If current value was scheduled, set to open
    if (statusSelect.value === scheduledValue) {
      statusSelect.value = 'open'
    }
    statusSelect.removeAttribute('disabled')
  }
  
  // Observe schedule changes and update due date/status field visibility
  observeScheduleChanges() {
    const observer = new MutationObserver(() => {
      const hasSchedules = this.visibleScheduleCount > 0
      
      if (hasSchedules) {
        this.hideDuedateField()
        this.disableStatusField()
      } else {
        if (this.hasStatusSelectTarget && this.statusSelectTarget.value !== 'scheduled') {
          this.showDuedateField()
        }
        this.enableStatusField()
      }
    })
    
    if (this.hasSchedulesContainerTarget) {
      observer.observe(this.schedulesContainerTarget, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      })
    }
  }
  
  // Add new schedule
  addNewSchedule(event) {
    event.preventDefault()
    
    const template = this.hasScheduleTemplateTarget ? this.scheduleTemplateTarget : document.getElementById("schedule-template")
    if (!template) return
    
    // Generate unique index and ID
    const timestamp = new Date().getTime()
    const randomPart = Math.floor(Math.random() * 10000)
    const scheduleIndex = `new_${timestamp}_${randomPart}`
    const scheduleId = `schedule-${timestamp}-${randomPart}`
    
    // Clone template
    const scheduleNode = document.importNode(template.content, true)
    const scheduleContainer = scheduleNode.querySelector(".task-schedule-container")
    scheduleContainer.id = scheduleId
    
    // Update name attributes with the specific index for this schedule
    const prefix = `task[task_schedules_attributes][${scheduleIndex}]`
    scheduleNode.querySelectorAll("[name^='SCHEDULE_PREFIX']").forEach(input => {
      const newName = input.getAttribute("name").replace("SCHEDULE_PREFIX", prefix)
      input.setAttribute("name", newName)
    })
    
    // Generate rule_name with UUID
    const ruleNameInput = scheduleNode.querySelector(".schedule-rule-name")
    if (ruleNameInput) {
      ruleNameInput.value = `task-${this.generateUuid()}`
    }
    
    // Set default schedule data
    const scheduleDataInput = scheduleNode.querySelector('[data-task-schedule-target="scheduleData"]')
    if (scheduleDataInput) {
      const defaultScheduleData = {
        frequency: { value: 1, type: "week" },
        days: { monday: ["09:00"] },
        endType: "never",
        duedateDay: 0,
        weekendAllowed: false
      };
      
      scheduleDataInput.value = JSON.stringify(defaultScheduleData)
      
      // Set default values for duedate_day and weekend_allowed fields
      const duedateDayField = scheduleNode.querySelector('[data-task-schedule-target="duedateDay"]')
      if (duedateDayField) {
        duedateDayField.value = defaultScheduleData.duedateDay || 0
      }
      
      const weekendAllowedField = scheduleNode.querySelector('[data-task-schedule-target="weekendAllowedHidden"]')
      if (weekendAllowedField) {
        weekendAllowedField.value = defaultScheduleData.weekendAllowed ? '1' : '0'
      }
    }
    
    // Update radio button IDs and names to be unique for this schedule
    scheduleNode.querySelectorAll("input[type='radio'][name='endType_SCHEDULE_INDEX']").forEach(radio => {
      // Create a unique name for the radio button group
      const newName = `endType_${scheduleIndex}`
      radio.setAttribute("name", newName)
      
      // Update the ID to be unique
      const oldId = radio.getAttribute("id")
      const newId = oldId.replace("SCHEDULE_INDEX", scheduleIndex)
      radio.setAttribute("id", newId)
      
      // Find and update corresponding label
      const label = scheduleNode.querySelector(`label[for='${oldId}']`)
      if (label) {
        label.setAttribute("for", newId)
      }
    })
    
    // Add to DOM and initialize
    this.schedulesContainerTarget.appendChild(scheduleNode)
    
    // Initialize the Stimulus controller for the new schedule (scheduleContainer is from the clone)
    const controller = this.application.getControllerForElementAndIdentifier(scheduleContainer, "task-schedule")
    if (controller && typeof controller.initialize === 'function') {
      controller.initialize()
    }
    
    // Always hide due date field when schedule is added (recurring tasks get duedate from schedule)
    this.hideDuedateField()
    
    // Disable status field - with scheduling, status must remain "scheduled"
    this.disableStatusField()
  }
  
  // Handle schedule deletion
  handleDeleteSchedule(event) {
    const button = event.currentTarget
    const container = button.closest(".task-schedule-container")
    const destroyCheckbox = container?.querySelector(".destroy-schedule-checkbox")
    
    if (destroyCheckbox) {
      destroyCheckbox.checked = true
      container.classList.add("d-none")
    } else if (container) {
      container.remove()
    }
    
    const remainingSchedules = this.visibleScheduleCount
    const taskId = this.element.dataset.taskId
    const isExistingTask = taskId && taskId !== ''
    
    // Show due date field if no schedules remain and status is not scheduled
    if (remainingSchedules === 0 && this.hasStatusSelectTarget && this.statusSelectTarget.value !== 'scheduled') {
      this.showDuedateField()
    }
    
    // Re-enable status field when no schedules remain
    if (remainingSchedules === 0) {
      this.enableStatusField()
    } else {
      this.disableStatusField()
    }
    
    // Re-enable main form fields if applicable
    if (this.hasStatusSelectTarget && this.statusSelectTarget.value !== 'scheduled' && (!isExistingTask || remainingSchedules === 0)) {
      this.enableMainFormFields()
    }
  }
  
  // Generate a UUID v4 format string
  generateUuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0
      const v = c === 'x' ? r : (r & 0x3 | 0x8)
      return v.toString(16)
    })
  }

  // Initialize existing schedules
  initExistingSchedules() {
    const validSchedules = this.scheduleContainerTargets.filter(container => {
      const scheduleDataInput = container.querySelector(".schedule-data")
      if (!scheduleDataInput) return false
      const value = scheduleDataInput.value
      return value && value !== '{}' && value !== 'null'
    })
    
    validSchedules.forEach(container => {
      const scheduleDataInput = container.querySelector(".schedule-data")
      if (scheduleDataInput) {
        setTimeout(() => {
          const controller = this.application.getControllerForElementAndIdentifier(container, "task-schedule")
          if (controller?.initializeSchedule) {
            controller.initializeSchedule()
          }
        }, 100)
      }
    })
  }
} 